export interface AiRecommendation {
  title: string;
  text: string;
  priority: "Высокий" | "Средний" | "Низкий";
}

export interface CrossSellProduct {
  group: string;
  product: string;
  reason: string;
  potentialAmount: number;
}

export interface UnpaidInvoice {
  id: string;
  number: string;
  date: string;
  amount: number;
  dueDate: string;
  daysOverdue: number;
}

export interface ChurnRiskFactor {
  name: string;
  weight: number;
  score: number;
  contribution: number;
  description: string;
  formula: string;
}

export interface ReorderScoreItem {
  product: string;
  group: string;
  daysSinceLastPurchase: number;
  avgIntervalDays: number;
  score: number;
  status: "рано" | "пик" | "просрочен" | "потерян";
  action: string;
  lastPurchaseDate: string;
}

export interface Client360Data {
  name: string;
  tags: string[];
  inn: string;
  kpp: string;
  bin: string;
  legalAddress: string;
  managerName: string;
  managerPhone: string;
  managerEmail: string;
  managerAvatar: string;
  sales12m: number;
  debt: number;
  lastPurchaseDate: string;
  partnerType: string;
  partnerSince: string;
  partnerStatus: string;
  segment: string;
  region: string;
  source: string;
  comment: string;
  tagsExtra: string[];
  currentDebt: number;
  overdueDebt: number;
  creditLimit: number;
  availableLimit: number;
  interactions: Interaction[];
  nextAction: {
    title: string;
    date: string;
    time: string;
    type: string;
    assignee: string;
  };
  contracts: Contract[];
  salesDynamics: SalesPeriod[];
  salesChart: number[];
  topProducts: TopProduct[];
  processSteps: ProcessStep[];
  dealChecklist: string[];
  managerWorkspace: string[];
  managerTips: string[];
  deals: Deal[];
  contacts: ContactPerson[];
  payments: PaymentRecord[];
  debtsDetail: DebtItem[];
  clientProducts: ClientProductPrice[];
  documents: ClientDocument[];
  files: ClientFile[];
  tasks: ClientTask[];
  notes: ClientNote[];
  purchaseFrequency: string;
  avgCheck: number;
  purchaseTrend: "рост" | "падение" | "стабильно";
  purchaseTrendPct: number;
  churnRisk: number;
  churnRiskLevel: "низкий" | "средний" | "высокий";
  churnRiskFactors: ChurnRiskFactor[];
  repeatSalePotential: number;
  repeatSaleDescription: string;
  reorderScores: ReorderScoreItem[];
  aiRecommendations: AiRecommendation[];
  crossSellProducts: CrossSellProduct[];
  unpaidInvoices: UnpaidInvoice[];
  clientStatusOptions: string[];
}

export interface Interaction {
  type: string;
  icon: string;
  title: string;
  date: string;
  time: string;
  author: string;
}

export interface Contract {
  name: string;
  type: string;
  status: string;
  debt: number;
  organization: string;
  isPrimary: boolean;
}

export interface SalesPeriod {
  period: string;
  amount: number;
  orders: number;
}

export interface TopProduct {
  group: string;
  amount: number;
}

export interface ProcessStep {
  label: string;
  icon: string;
}

export interface Deal {
  id: string;
  type: string;
  title: string;
  date: string;
  amount: number;
  status: string;
  stage: string;
  manager: string;
  products: string;
}

export interface ContactPerson {
  name: string;
  role: string;
  phone: string;
  email: string;
  isDecisionMaker: boolean;
}

export interface PaymentRecord {
  id: string;
  date: string;
  docNumber: string;
  type: string;
  amount: number;
  status: string;
}

export interface DebtItem {
  label: string;
  amount: number;
  overdue: boolean;
}

export interface ClientProductPrice {
  group: string;
  product: string;
  article: string;
  price: number;
  unit: string;
  minOrder: number;
}

export interface ClientDocument {
  id: string;
  type: string;
  number: string;
  date: string;
  amount: number;
  status: string;
  author: string;
}

export interface ClientFile {
  name: string;
  type: string;
  size: string;
  date: string;
  author: string;
}

export interface ClientTask {
  id: string;
  title: string;
  dueDate: string;
  priority: string;
  status: string;
  assignee: string;
}

export interface ClientNote {
  id: string;
  text: string;
  date: string;
  author: string;
}

export const client360Data: Client360Data = {
  name: "ООО «ТекстильПро»",
  tags: ["Основной контрагент"],
  inn: "7701234567",
  kpp: "770101001",
  bin: "1027700132195",
  legalAddress: "123456, г. Москва, ул. Текстильщиков, д. 15, офис 301",
  managerName: "Иванов А.А.",
  managerPhone: "+7 (495) 123-45-67",
  managerEmail: "manager@textilpro.ru",
  managerAvatar: "https://readdy.ai/api/search-image?query=Professional%20business%20man%20portrait%20headshot%20in%20formal%20suit%20smiling%20neutral%20background%20clean%20corporate%20style%20high%20quality&width=100&height=100&seq=avatar-ivanov&orientation=squarish",
  sales12m: 30250000,
  debt: 2350000,
  lastPurchaseDate: "15.05.2025",
  partnerType: "Дистрибьютор",
  partnerSince: "12.03.2022",
  partnerStatus: "Активный",
  segment: "Крупный опт",
  region: "Москва",
  source: "Выставка",
  comment: "Крупный производитель одежды, закупает ткани и фурнитуру оптом",
  tagsExtra: ["Оптовик", "Постоянный"],
  currentDebt: 2350000,
  overdueDebt: 1250000,
  creditLimit: 5000000,
  availableLimit: 2650000,
  interactions: [
    {
      type: "whatsapp",
      icon: "ri-whatsapp-line",
      title: "Запросили цену на ткань лён ЛН-200, 500 м",
      date: "15.05.2025",
      time: "11:32",
      author: "Иванов А.А.",
    },
    {
      type: "call",
      icon: "ri-phone-line",
      title: "Исходящий звонок. Обсудили поставку, договорились отправить КП",
      date: "14.05.2025",
      time: "16:20",
      author: "Иванов А.А.",
    },
    {
      type: "email",
      icon: "ri-mail-line",
      title: "Отправлено КП НКП-000152 на ткань х/б и подкладку",
      date: "14.05.2025",
      time: "10:15",
      author: "Иванов А.А.",
    },
  ],
  nextAction: {
    title: "Перезвонить и уточнить решение по КП НКП-000152",
    date: "18.05.2025",
    time: "10:00",
    type: "Звонок",
    assignee: "Иванов А.А.",
  },
  contracts: [
    {
      name: "ООО «ТекстильПро»",
      type: "Опт",
      status: "Активный",
      debt: 2350000,
      organization: "ООО «ТекстильПро»",
      isPrimary: true,
    },
    {
      name: "ООО «ТекстильПро-Сибирь»",
      type: "Опт-1",
      status: "Активный",
      debt: 480000,
      organization: "ООО «ТекстильПро»",
      isPrimary: false,
    },
    {
      name: "ООО «ТекстильПро-Юг»",
      type: "Мелкий опт",
      status: "Активный",
      debt: 0,
      organization: "ООО «ТекстильПро»",
      isPrimary: false,
    },
    {
      name: "ИП Петров С.И.",
      type: "Розница",
      status: "Не используется",
      debt: 0,
      organization: "ООО «ТекстильПро»",
      isPrimary: false,
    },
  ],
  salesDynamics: [
    { period: "За 30 дней", amount: 3250000, orders: 4 },
    { period: "За 90 дней", amount: 8750000, orders: 11 },
    { period: "За 12 месяцев", amount: 30250000, orders: 18 },
  ],
  salesChart: [1.2, 1.5, 1.3, 1.8, 2.0, 1.9, 2.4, 2.8, 2.5, 3.1, 3.5, 3.2],
  topProducts: [
    { group: "Ткань х/б", amount: 9250000 },
    { group: "Ткань лён", amount: 3150000 },
    { group: "Нитки полиэстер", amount: 1250000 },
    { group: "Молнии YKK", amount: 900000 },
    { group: "Ткань подкладочная", amount: 700000 },
  ],
  processSteps: [
    { label: "CRM 360 клиента", icon: "ri-user-search-line" },
    { label: "Сделка", icon: "ri-shake-hands-line" },
    { label: "Заказ покупателя / Счет", icon: "ri-file-list-2-line" },
    { label: "Оплата / Отгрузка", icon: "ri-truck-line" },
  ],
  dealChecklist: [
    "Клиента",
    "Основную дочернюю компанию (по умолчанию)",
    "Ответственного менеджера",
    "Источник / сегмент клиента",
    "Потребности клиента",
    "Этап продажи",
    "Планируемую сумму",
    "Следующее действие / дату",
    "Связанные коммуникации",
    "Связанные документы заказа / счета / оплаты",
  ],
  managerWorkspace: [
    "Основное рабочее место — CRM 360 клиента",
    "Здесь он звонит, пишет, ставит задачи, видит долг и следующее действие",
    "Из этой карточки создает сделку, заказ, счет",
    "Документ сделки используется для ведения этапа продажи и контроля процесса",
  ],
  managerTips: [
    "Вся информация по клиенту в одном окне",
    "Понимание финансовой ситуации",
    "Быстрый доступ к истории и задачам",
    "Понимание, на кого выписывать документы",
  ],
  deals: [
    {
      id: "СД-000152",
      type: "Сделка",
      title: "Поставка ткани х/б и льна на осеннюю коллекцию",
      date: "12.05.2025",
      amount: 4850000,
      status: "В работе",
      stage: "Согласование КП",
      manager: "Иванов А.А.",
      products: "Ткань х/б (1000 м), Ткань лён (500 м)",
    },
    {
      id: "СД-000148",
      type: "Сделка",
      title: "Закупка фурнитуры и ниток для нового цеха",
      date: "05.05.2025",
      amount: 1250000,
      status: "Выставлен счет",
      stage: "Ожидание оплаты",
      manager: "Иванов А.А.",
      products: "Нитки полиэстер (200 кат.), Молнии YKK (500 шт.)",
    },
    {
      id: "ЗК-000321",
      type: "Заказ",
      title: "Заказ покупателя №321 от 28.04.2025",
      date: "28.04.2025",
      amount: 2100000,
      status: "Отгружен",
      stage: "Закрыт",
      manager: "Иванов А.А.",
      products: "Ткань подкладочная (800 м), Ткань х/б (500 м)",
    },
    {
      id: "ЗК-000318",
      type: "Заказ",
      title: "Заказ покупателя №318 от 15.04.2025",
      date: "15.04.2025",
      amount: 875000,
      status: "Отгружен",
      stage: "Закрыт",
      manager: "Иванов А.А.",
      products: "Молнии YKK (300 шт.), Нитки полиэстер (100 кат.)",
    },
    {
      id: "СД-000139",
      type: "Сделка",
      title: "Пробная партия ткани лён для нового филиала в Новосибирске",
      date: "01.04.2025",
      amount: 620000,
      status: "Закрыта",
      stage: "Оплачено",
      manager: "Иванов А.А.",
      products: "Ткань лён (200 м)",
    },
    {
      id: "ЗК-000305",
      type: "Заказ",
      title: "Заказ покупателя №305 от 10.03.2025",
      date: "10.03.2025",
      amount: 3200000,
      status: "Отгружен",
      stage: "Закрыт",
      manager: "Иванов А.А.",
      products: "Ткань х/б (1500 м), Ткань подкладочная (600 м), Нитки полиэстер (300 кат.)",
    },
  ],
  contacts: [
    { name: "Петров Сергей Иванович", role: "Генеральный директор", phone: "+7 (495) 234-56-78", email: "petrov@textilpro.ru", isDecisionMaker: true },
    { name: "Смирнова Анна Павловна", role: "Коммерческий директор", phone: "+7 (495) 234-56-79", email: "smirnova@textilpro.ru", isDecisionMaker: true },
    { name: "Козлов Дмитрий Алексеевич", role: "Начальник отдела закупок", phone: "+7 (495) 234-56-80", email: "kozlov@textilpro.ru", isDecisionMaker: false },
    { name: "Николаева Елена Викторовна", role: "Бухгалтер", phone: "+7 (495) 234-56-81", email: "nikolaeva@textilpro.ru", isDecisionMaker: false },
    { name: "Фёдоров Алексей Петрович", role: "Заведующий складом", phone: "+7 (495) 234-56-82", email: "fedorov@textilpro.ru", isDecisionMaker: false },
  ],
  payments: [
    { id: "ПП-000452", date: "15.05.2025", docNumber: "СФ-000152", type: "Оплата от клиента", amount: 2100000, status: "Проведено" },
    { id: "ПП-000448", date: "28.04.2025", docNumber: "СФ-000148", type: "Оплата от клиента", amount: 1250000, status: "Проведено" },
    { id: "ПП-000435", date: "10.04.2025", docNumber: "СФ-000139", type: "Оплата от клиента", amount: 620000, status: "Проведено" },
    { id: "ПП-000421", date: "05.03.2025", docNumber: "СФ-000128", type: "Оплата от клиента", amount: 3200000, status: "Проведено" },
    { id: "ПД-000089", date: "20.04.2025", docNumber: "—", type: "Возврат клиенту", amount: 150000, status: "Проведено" },
    { id: "ПП-000411", date: "15.02.2025", docNumber: "СФ-000115", type: "Оплата от клиента", amount: 1800000, status: "Проведено" },
  ],
  debtsDetail: [
    { label: "Текущий долг", amount: 2350000, overdue: false },
    { label: "Просроченная задолженность", amount: 1250000, overdue: true },
    { label: "Счёт СФ-000152 (12.05.2025)", amount: 1100000, overdue: false },
    { label: "Счёт СФ-000148 (05.05.2025)", amount: 850000, overdue: true },
    { label: "Счёт СФ-000139 (01.04.2025)", amount: 400000, overdue: true },
  ],
  clientProducts: [
    { group: "Ткань х/б", product: "Бязь отбеленная (120 г/м²)", article: "ТК-00120", price: 1850, unit: "м", minOrder: 100 },
    { group: "Ткань х/б", product: "Сатин гладкокрашеный (140 г/м²)", article: "ТК-00140", price: 2450, unit: "м", minOrder: 50 },
    { group: "Ткань х/б", product: "Поплин набивной (110 г/м²)", article: "ТК-00110", price: 1680, unit: "м", minOrder: 100 },
    { group: "Ткань лён", product: "Лён суровый ЛН-200", article: "ЛН-00200", price: 3200, unit: "м", minOrder: 50 },
    { group: "Ткань лён", product: "Лён отбеленный ЛН-180", article: "ЛН-00180", price: 2950, unit: "м", minOrder: 50 },
    { group: "Нитки полиэстер", product: "Нить 40/2 (белая)", article: "НП-4002", price: 680, unit: "кат.", minOrder: 10 },
    { group: "Нитки полиэстер", product: "Нить 50/2 (чёрная)", article: "НП-5002", price: 650, unit: "кат.", minOrder: 10 },
    { group: "Молнии YKK", product: "Молния спираль №5 (метраж)", article: "МЛ-YK05", price: 420, unit: "м", minOrder: 100 },
    { group: "Молнии YKK", product: "Молния трактор №8 (метраж)", article: "МЛ-YK08", price: 580, unit: "м", minOrder: 50 },
    { group: "Ткань подкладочная", product: "Подкладка вискозная (90 г/м²)", article: "ПД-0090", price: 950, unit: "м", minOrder: 100 },
  ],
  documents: [
    { id: "СФ-000152", type: "Счёт-фактура", number: "СФ-000152", date: "12.05.2025", amount: 4850000, status: "Выставлен", author: "Иванов А.А." },
    { id: "СФ-000148", type: "Счёт-фактура", number: "СФ-000148", date: "05.05.2025", amount: 1250000, status: "Выставлен", author: "Иванов А.А." },
    { id: "РН-000321", type: "Расходная накладная", number: "РН-000321", date: "28.04.2025", amount: 2100000, status: "Отгружен", author: "Иванов А.А." },
    { id: "СФ-000139", type: "Счёт-фактура", number: "СФ-000139", date: "01.04.2025", amount: 620000, status: "Оплачен", author: "Иванов А.А." },
    { id: "ДГ-00045", type: "Договор поставки", number: "ДГ-00045", date: "12.03.2022", amount: 0, status: "Действует", author: "Иванов А.А." },
    { id: "АКТ-00089", type: "Акт сверки", number: "АКТ-00089", date: "01.06.2025", amount: 0, status: "Подписан", author: "Николаева Е.В." },
    { id: "РН-000318", type: "Расходная накладная", number: "РН-000318", date: "15.04.2025", amount: 875000, status: "Отгружен", author: "Иванов А.А." },
  ],
  files: [
    { name: "Договор_ТекстильПро_2022.pdf", type: "PDF", size: "2.4 МБ", date: "12.03.2022", author: "Иванов А.А." },
    { name: "Реквизиты_ТекстильПро.xlsx", type: "Excel", size: "48 КБ", date: "15.01.2025", author: "Николаева Е.В." },
    { name: "КП_осенняя_коллекция_2025.pdf", type: "PDF", size: "1.8 МБ", date: "12.05.2025", author: "Иванов А.А." },
    { name: "Сертификаты_качества_ткани.pdf", type: "PDF", size: "3.2 МБ", date: "10.03.2025", author: "Иванов А.А." },
    { name: "Акт_сверки_01.06.2025.pdf", type: "PDF", size: "560 КБ", date: "01.06.2025", author: "Николаева Е.В." },
    { name: "Фото_образцов_ткани_лён.zip", type: "ZIP", size: "12.5 МБ", date: "18.04.2025", author: "Иванов А.А." },
  ],
  tasks: [
    { id: "ЗД-00045", title: "Перезвонить и уточнить решение по КП НКП-000152", dueDate: "18.05.2025", priority: "Высокий", status: "В работе", assignee: "Иванов А.А." },
    { id: "ЗД-00042", title: "Подготовить договор на новую линейку тканей", dueDate: "20.05.2025", priority: "Средний", status: "Новая", assignee: "Иванов А.А." },
    { id: "ЗД-00039", title: "Отправить сертификаты качества на ткань лён", dueDate: "10.05.2025", priority: "Высокий", status: "Выполнена", assignee: "Иванов А.А." },
    { id: "ЗД-00036", title: "Согласовать скидку 5% на объём от 5000 м", dueDate: "05.05.2025", priority: "Средний", status: "Выполнена", assignee: "Иванов А.А." },
    { id: "ЗД-00033", title: "Выставить счёт на пробную партию для филиала", dueDate: "28.04.2025", priority: "Высокий", status: "Выполнена", assignee: "Иванов А.А." },
  ],
  notes: [
    { id: "ПР-00012", text: "Клиент планирует расширение производства в 2026 году — ожидается рост закупок на 30-40%. Важно удержать цены на текущем уровне.", date: "15.05.2025", author: "Иванов А.А." },
    { id: "ПР-00011", text: "Обсудили с коммерческим директором возможность эксклюзивного договора на ткань лён. Ждут решения от генерального до конца месяца.", date: "10.05.2025", author: "Иванов А.А." },
    { id: "ПР-00010", text: "Клиент жалуется на долгую доставку в Новосибирск (филиал). Предложил рассмотреть склад ответственного хранения в регионе.", date: "28.04.2025", author: "Иванов А.А." },
    { id: "ПР-00009", text: "Смирнова А.П. (коммерческий директор) уходит в отпуск с 01.06 по 15.06. Все вопросы по закупкам — на Козлова Д.А.", date: "25.04.2025", author: "Иванов А.А." },
    { id: "ПР-00008", text: "Провели встречу на производстве. Понравилось качество отгрузки. Обсудили возможность долгосрочного контракта на 2 года.", date: "15.04.2025", author: "Иванов А.А." },
  ],
  purchaseFrequency: "2-3 раза в месяц",
  avgCheck: 1680000,
  purchaseTrend: "рост",
  purchaseTrendPct: 18,
  churnRisk: 12,
  churnRiskLevel: "низкий",
  churnRiskFactors: [
    {
      name: "Давность последней покупки",
      weight: 0.30,
      score: 5,
      contribution: 1.5,
      description: "Клиент активен, последняя покупка 15.05.2025",
      formula: "(ДнейПрошло / СреднийИнтервалЗакупок − 1) × 100",
    },
    {
      name: "Динамика частоты",
      weight: 0.20,
      score: 5,
      contribution: 1.0,
      description: "Частота закупок стабильна, рост +18% за 90 дней",
      formula: "−((Частота_90дн − Частота_пред90дн) / Частота_пред90дн) × 100",
    },
    {
      name: "Динамика сумм",
      weight: 0.20,
      score: 5,
      contribution: 1.0,
      description: "Объёмы закупок растут, рост +18%",
      formula: "−((Продажи_90дн − Продажи_пред90дн) / Продажи_пред90дн) × 100",
    },
    {
      name: "Долговая нагрузка",
      weight: 0.15,
      score: 45,
      contribution: 6.75,
      description: "Просрочка 1.25 млн из 2.35 млн долга",
      formula: "(Просрочка / ТекущийДолг) × 100",
    },
    {
      name: "Реакция на контакты",
      weight: 0.15,
      score: 15,
      contribution: 2.25,
      description: "Отвечает на КП и звонки в течение 1-2 дней",
      formula: "min(100, ДнейБезОтветаНаКП × 5)",
    },
  ],
  repeatSalePotential: 78,
  repeatSaleDescription: "Высокая вероятность повторной закупки ткани х/б и льна в ближайшие 30 дней. Планируемое расширение производства увеличит объёмы на 30-40%.",
  reorderScores: [
    {
      product: "Бязь отбеленная (120 г/м²)",
      group: "Ткань х/б",
      daysSinceLastPurchase: 17,
      avgIntervalDays: 20,
      score: 85,
      status: "пик",
      action: "Показать как рекомендацию («ожидаем закупку»)",
      lastPurchaseDate: "23.04.2025",
    },
    {
      product: "Лён суровый ЛН-200",
      group: "Ткань лён",
      daysSinceLastPurchase: 5,
      avgIntervalDays: 25,
      score: 20,
      status: "рано",
      action: "Не показывать",
      lastPurchaseDate: "15.05.2025",
    },
    {
      product: "Нить 40/2 (белая)",
      group: "Нитки полиэстер",
      daysSinceLastPurchase: 58,
      avgIntervalDays: 40,
      score: 145,
      status: "просрочен",
      action: "Триггер «стоит позвонить»",
      lastPurchaseDate: "22.03.2025",
    },
    {
      product: "Молния спираль №5",
      group: "Молнии YKK",
      daysSinceLastPurchase: 95,
      avgIntervalDays: 35,
      score: 271,
      status: "потерян",
      action: "Уходит в фактор риска ухода",
      lastPurchaseDate: "15.02.2025",
    },
  ],
  aiRecommendations: [
    {
      title: "Предложить эксклюзивный договор на ткань лён",
      text: "Клиент заинтересован в стабильных поставках льна. Эксклюзивный договор на 12 мес. с фиксацией цены увеличит лояльность и объём закупок на 25-30%.",
      priority: "Высокий",
    },
    {
      title: "Создать склад ответственного хранения в Новосибирске",
      text: "Филиал в Сибири испытывает проблемы с доставкой. Склад в регионе сократит сроки с 14 до 3 дней и снизит риск потери клиента.",
      priority: "Средний",
    },
    {
      title: "Предложить скидку 7% при заказе от 10000 м",
      text: "Текущий средний чек — 1.68 млн ₽. При стимулировании объёма скидкой прогнозируемый рост среднего чека до 2.5 млн ₽.",
      priority: "Средний",
    },
    {
      title: "Напомнить о новой линейке фурнитуры",
      text: "В июне ожидается поступление новой коллекции пуговиц и кнопок. Клиент закупает фурнитуру — высокая вероятность допродажи.",
      priority: "Низкий",
    },
  ],
  crossSellProducts: [
    { group: "Фурнитура", product: "Пуговицы пластиковые (набор 100 шт.)", reason: "Клиент закупает ткань для пошива одежды — пуговицы идут в комплекте", potentialAmount: 320000 },
    { group: "Фурнитура", product: "Кнопки металлические (набор 50 шт.)", reason: "Дополнение к ткани для верхней одежды и спецодежды", potentialAmount: 180000 },
    { group: "Упаковка", product: "Пакеты брендированные с логотипом", reason: "Клиент развивает розничное направление — нужна фирменная упаковка", potentialAmount: 250000 },
    { group: "Ткань", product: "Ткань смесовая для спецодежды", reason: "Новое направление клиента — пошив рабочей одежды для партнёров", potentialAmount: 1450000 },
  ],
  unpaidInvoices: [
    { id: "СФ-000152", number: "СФ-000152", date: "12.05.2025", amount: 1100000, dueDate: "19.05.2025", daysOverdue: 0 },
    { id: "СФ-000148", number: "СФ-000148", date: "05.05.2025", amount: 850000, dueDate: "12.05.2025", daysOverdue: 7 },
    { id: "СФ-000139", number: "СФ-000139", date: "01.04.2025", amount: 400000, dueDate: "08.04.2025", daysOverdue: 41 },
  ],
  clientStatusOptions: ["Новый", "Активный", "Уснувший", "Потерянный", "VIP", "Должник"],
};

export const client360Tabs = [
  "Обзор",
  "Контакты",
  "Клиент и дочерние компании",
  "История взаимодействий",
  "Сделки и заказы",
  "Оплаты и долги",
  "Товары и цены",
  "Документы",
  "Файлы",
  "Задачи",
  "Примечания",
];